import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, X, Loader2, ChevronDown } from 'lucide-react';
import { api } from '../services/api';

// LocationSelector Component
export const LocationSelector = ({ 
  initialLocation = null, 
  onLocationChange = () => {},
  allowCountryChange = true,
  placeholder = "Search for your city...",
  className = "",
  showCountryFirst = false,
  api = null // Pass in your api instance
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [error, setError] = useState(null);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Handle search with debouncing
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Build the query params
        const params = { q: query };
        if (!allowCountryChange && selectedLocation?.country) {
          params.country = selectedLocation.country;
        }
        
        // Use the api service which already has auth headers configured
        const response = await api.get('/location/search-locations', { params });
        
        setSuggestions(response.data.locations || []);
        setShowDropdown(true);
        setHighlightedIndex(-1);
      } catch (err) {
        console.error('Location search error:', err);
        setError('Failed to search locations. Please try again.');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimerRef.current);
  }, [query, allowCountryChange, selectedLocation?.country]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleLocationSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle location selection
  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);
    setQuery('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
    
    // Notify parent component
    onLocationChange(location);
  };

  // Format location display
  const formatLocationDisplay = (location) => {
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.state_province) parts.push(location.state_province);
    if (location.country) parts.push(location.country);
    return parts.join(', ');
  };

  // Get flag emoji for country
  const getCountryFlag = (country) => {
    const flags = {
      'United States': '🇺🇸',
      'Canada': '🇨🇦',
      'Mexico': '🇲🇽',
      'United Kingdom': '🇬🇧',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Spain': '🇪🇸',
      'Italy': '🇮🇹',
      'China': '🇨🇳',
      'Japan': '🇯🇵',
      'South Korea': '🇰🇷',
      'Australia': '🇦🇺',
      'New Zealand': '🇳🇿',
    };
    return flags[country] || '🌍';
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected Location Display */}
      {selectedLocation && (
        <div className="mb-2">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent-tint text-accent text-sm">
            <span className="mr-2">{getCountryFlag(selectedLocation.country)}</span>
            <span className="font-medium">{formatLocationDisplay(selectedLocation)}</span>
            <button
              onClick={() => {
                setSelectedLocation(null);
                onLocationChange(null);
              }}
              className="ml-2 hover:text-accent-hover"
              aria-label="Clear location"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full px-4 py-2 pr-10 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all"
          aria-label="Location search"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
        />
        
        {/* Search Icon / Loading Spinner */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {loading ? (
            <Loader2 className="animate-spin h-5 w-5 text-ink-muted" />
          ) : (
            <Search className="h-5 w-5 text-ink-muted" />
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-2 bg-surface-card border border-border-default rounded-card shadow-elevation-1 max-h-60 overflow-auto"
          role="listbox"
        >
          {suggestions.map((location, index) => (
            <div
              key={`${location.city}-${location.postal_code}-${index}`}
              className={`px-4 py-3 cursor-pointer hover:bg-accent-tint transition-colors ${
                index === highlightedIndex ? 'bg-accent-tint' : ''
              }`}
              onClick={() => handleLocationSelect(location)}
              onMouseEnter={() => setHighlightedIndex(index)}
              role="option"
              aria-selected={index === highlightedIndex}
            >
              <div className="flex items-start">
                <span className="mr-3 mt-0.5 text-lg">
                  {getCountryFlag(location.country)}
                </span>
                <div className="flex-1">
                  <div className="font-medium text-ink-soft">
                    {location.city}
                  </div>
                  <div className="text-sm text-ink-muted">
                    {location.state_province}, {location.country}
                  </div>
                  {location.postal_code && (
                    <div className="text-small text-ink-muted mt-0.5">
                      {location.postal_code}
                    </div>
                  )}
                </div>
                <MapPin className="w-4 h-4 text-ink-muted mt-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showDropdown && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-2 bg-surface-card border border-border-default rounded-card shadow-elevation-1 p-4">
          <div className="text-ink-muted text-center">
            No locations found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
};

// Country Selector Component (for registration)
export const CountrySelector = ({ 
  value, 
  onChange, 
  className = "",
  error = null 
}) => {
  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    // Add more countries as they become available
  ];

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-ink-soft mb-1">
        Country
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`appearance-none w-full px-4 py-3 pr-10 border ${
            error ? 'border-danger' : 'border-border-strong'
          } rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all`}
        >
          <option value="">Select your country</option>
          {countries.map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-muted pointer-events-none" />
      </div>
      {error && (
        <p className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
};

// Demo Component
export default function LocationSelectorDemo() {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('US');

  return (
    <div className="min-h-screen bg-surface-page p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Country Selector Example (for Registration) */}
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
          <h2 className="text-2xl font-bold text-ink-soft mb-4">
            Registration Country Selection
          </h2>
          <CountrySelector 
            value={selectedCountry}
            onChange={setSelectedCountry}
          />
          {selectedCountry && (
            <div className="mt-4 p-3 bg-success-tint rounded-lg">
              <p className="text-sm text-success">
                Selected country: {selectedCountry}
              </p>
            </div>
          )}
        </div>

        {/* Full Location Selector (for Profile) */}
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
          <h2 className="text-2xl font-bold text-ink-soft mb-4">
            Profile Location Settings
          </h2>
          <LocationSelector
            initialLocation={selectedLocation}
            onLocationChange={(location) => {
              setSelectedLocation(location);
              console.log('Location changed:', location);
            }}
            placeholder="Type to search for your city..."
          />
          
          {selectedLocation && (
            <div className="mt-6 p-4 bg-info-tint rounded-lg">
              <h3 className="font-semibold text-info mb-2">Selected Location</h3>
              <div className="text-sm text-info space-y-1">
                <div>City: {selectedLocation.city}</div>
                <div>State/Province: {selectedLocation.state_province}</div>
                <div>Country: {selectedLocation.country}</div>
                {selectedLocation.postal_code && (
                  <div>Postal Code: {selectedLocation.postal_code}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}